import { useState } from "react";
import { ChevronDown, Clock, Keyboard, LogOut } from "lucide-react";
import { useAuth, useUserPhoto } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Avatar, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, MOD, ShortcutsDialog } from "@ncp/ui";
import { DemoClockDialog, useDemoClock } from "./DemoControls";

/** The account menu at the end of the header: who is signed in, the test clock where the host has one, and sign-out. */
export function AccountMenu({ name, title, onSignOut }: { name: string; title: string; onSignOut: () => void }) {
  const { t } = useTranslation();
  const clock = useDemoClock();
  const photo = useUserPhoto(useAuth().user?.photoUrl);
  const [clockOpen, setClockOpen] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);
  // The platform's keyboard shortcuts, all standard, listed in this one place (visual item 27).
  const shortcuts = [
    { keys: [MOD, "K"], label: t("backoffice:account.keys.search") },
    { keys: ["↑", "↓"], label: t("backoffice:account.keys.rows") },
    { keys: ["Enter"], label: t("backoffice:account.keys.open") },
    { keys: ["Esc"], label: t("backoffice:account.keys.close") },
  ];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="flex items-center gap-2 rounded-md px-1.5 py-1 text-start outline-none transition-colors hover:bg-sand-100 focus-visible:ring-2 focus-visible:ring-ring" aria-label={t("backoffice:account.menu")}>
            <Avatar name={name} src={photo} size="sm" />
            <span className="hidden min-w-0 leading-tight lg:block">
              <span className="block max-w-44 truncate text-sm font-semibold" title={name}>{name}</span>
              <span className="block max-w-44 truncate text-[11px] text-muted-foreground" title={title}>{title}</span>
            </span>
            <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="font-normal normal-case tracking-normal">
            <span className="block text-sm font-semibold">{name}</span>
            {title ? <span className="block text-xs text-muted-foreground">{title}</span> : null}
          </DropdownMenuLabel>
          {clock ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setClockOpen(true)}><Clock /> {t("backoffice:demo.title")}</DropdownMenuItem>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setKeysOpen(true)}><Keyboard /> {t("backoffice:account.keys.title")}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onSelect={onSignOut}><LogOut /> {t("actions.signOut")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {clock ? <DemoClockDialog open={clockOpen} onOpenChange={setClockOpen} /> : null}
      <ShortcutsDialog open={keysOpen} onOpenChange={setKeysOpen} title={t("backoffice:account.keys.title")} rows={shortcuts} />
    </>
  );
}
